import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import {
  getAchievementAudit,
  resolveHeldAchievement,
} from "@/lib/achievements.functions";
import { ACHIEVEMENTS, ACHIEVEMENT_RULES, AWARD_GUARDS } from "@/lib/achievements";
import { Button } from "@/components/ui/button";
import { ukDateTime } from "@/lib/format";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/owner/achievements")({
  head: () => ({
    meta: [
      { title: "Achievement audit — Shields Control Room" },
      { name: "description", content: "Why every achievement reward was granted or withheld." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: OwnerAchievements,
});

const OUTCOMES = ["all", "granted", "held", "rate_limited", "duplicate", "rejected"] as const;

const OUTCOME_STYLE: Record<string, string> = {
  granted: "bg-success/15 text-success",
  held: "bg-warning/20 text-warning-foreground",
  rate_limited: "bg-warning/20 text-warning-foreground",
  duplicate: "bg-muted text-muted-foreground",
  rejected: "bg-destructive/15 text-destructive",
};

function OwnerAchievements() {
  const [outcome, setOutcome] = useState<string>("all");
  const [busy, setBusy] = useState(false);
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["owner-achievement-audit", outcome],
    queryFn: () => getAchievementAudit({ data: { outcome } }),
    retry: false,
  });

  async function decide(achievementId: string, decision: "approve" | "reject") {
    setBusy(true);
    const res = await resolveHeldAchievement({ data: { achievementId, decision } });
    setBusy(false);
    if (!res.ok) {
      toast.error(res.error ?? "That didn't work");
      return;
    }
    toast.success(decision === "approve" ? "Reward approved and credited" : "Award rejected");
    await queryClient.invalidateQueries({ queryKey: ["owner-achievement-audit"] });
  }

  return (
    <div className="min-h-screen bg-background">
      <main className="section-pad mx-auto max-w-4xl py-10">
        <Link
          to="/owner"
          className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden /> Control Room
        </Link>

        <h1 className="mt-6 font-display text-3xl font-extrabold tracking-tight">
          Achievement audit trail
        </h1>
        <p className="mt-2 max-w-2xl text-muted-foreground">
          Every awarding decision, why it was made, what triggered it, and whether it was
          auto-approved or held for you.
        </p>

        {error && (
          <p className="mt-6 rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive">
            You need owner access to view this page.
          </p>
        )}

        {data && data.heldAwards.length > 0 && (
          <section className="surface-premium mt-6 p-5">
            <p className="flex items-center gap-3 text-sm font-bold">
              <ShieldCheck className="size-5 text-warning" aria-hidden />
              {data.heldAwards.length} award{data.heldAwards.length === 1 ? "" : "s"} held for your
              decision
            </p>
            <ul className="mt-4 space-y-3">
              {data.heldAwards.map((h) => (
                <li
                  key={h.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border px-4 py-3"
                >
                  <div>
                    <p className="text-sm font-semibold">
                      {ACHIEVEMENTS[h.achievement_key]?.title ?? h.achievement_key} ·{" "}
                      {h.subjectName}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Flagged {ukDateTime(h.awarded_at)} ·{" "}
                      {(ACHIEVEMENTS[h.achievement_key]?.credits ?? 0).toLocaleString()} SC at stake
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      className="btn-glow min-h-10 px-4 text-sm"
                      disabled={busy}
                      onClick={() => decide(h.id, "approve")}
                    >
                      Approve and credit
                    </Button>
                    <Button
                      variant="outline"
                      className="min-h-10"
                      disabled={busy}
                      onClick={() => decide(h.id, "reject")}
                    >
                      Reject
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-6 flex flex-wrap gap-2">
          {OUTCOMES.map((o) => (
            <button
              key={o}
              onClick={() => setOutcome(o)}
              className={`min-h-9 rounded-full px-4 text-xs font-bold uppercase tracking-wide transition ${
                outcome === o
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              {o.replace("_", " ")}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="surface-premium mt-6 h-64 animate-pulse rounded-2xl" />
        ) : (
          <ul className="mt-6 space-y-3">
            {(data?.entries ?? []).map((e) => (
              <li key={e.id} className="surface-premium p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-bold">
                      {ACHIEVEMENTS[e.achievement_key]?.title ?? e.achievement_key}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {e.subjectName} · {ukDateTime(e.created_at)}
                    </p>
                  </div>
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${
                      OUTCOME_STYLE[e.outcome] ?? "bg-muted text-muted-foreground"
                    }`}
                  >
                    {e.outcome.replace("_", " ")}
                  </span>
                </div>
                <p className="mt-3 text-sm">{e.reason}</p>
                <dl className="mt-3 grid gap-2 text-xs text-muted-foreground sm:grid-cols-3">
                  <div>
                    <dt className="font-semibold uppercase tracking-wide">Triggered by</dt>
                    <dd>{e.triggered_by ?? "system sweep"}</dd>
                  </div>
                  <div>
                    <dt className="font-semibold uppercase tracking-wide">Approval</dt>
                    <dd>{(e.approval_status ?? "auto_approved").replace("_", " ")}</dd>
                  </div>
                  <div>
                    <dt className="font-semibold uppercase tracking-wide">Credits</dt>
                    <dd>{(e.credits ?? 0).toLocaleString()} SC</dd>
                  </div>
                </dl>
                <p className="mt-2 break-all font-mono text-[11px] text-muted-foreground">
                  event: {e.event_key}
                </p>
                <details className="mt-3 rounded-xl border border-border bg-surface px-4 py-3">
                  <summary className="cursor-pointer text-xs font-bold uppercase tracking-wide text-muted-foreground">
                    Rules and event checks
                  </summary>
                  {(() => {
                    const rule = ACHIEVEMENT_RULES[e.achievement_key];
                    return (
                      <div className="mt-3 space-y-2 text-xs text-muted-foreground">
                        {rule ? (
                          <>
                            <p>
                              <span className="font-semibold text-foreground">Threshold:</span>{" "}
                              {rule.threshold} verified event
                              {rule.threshold === 1 ? "" : "s"} required.
                            </p>
                            <p>
                              <span className="font-semibold text-foreground">Recounted from:</span>{" "}
                              <span className="font-mono">{rule.source}</span>
                            </p>
                            <p>
                              <span className="font-semibold text-foreground">Event check:</span>{" "}
                              {rule.eventCheck}
                            </p>
                          </>
                        ) : (
                          <p>No rule definition found for this key.</p>
                        )}
                        <p>
                          <span className="font-semibold text-foreground">Event key:</span>{" "}
                          <span className="font-mono break-all">{e.event_key ?? "—"}</span> —
                          globally unique, so a replay can never re-credit.
                        </p>
                        <div>
                          <p className="font-semibold text-foreground">Guards applied</p>
                          <ul className="mt-1 list-disc space-y-1 pl-4">
                            {AWARD_GUARDS.map((g) => (
                              <li key={g}>{g}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    );
                  })()}
                </details>
              </li>
            ))}
            {data && data.entries.length === 0 && (
              <li className="surface-premium px-5 py-8 text-center text-sm text-muted-foreground">
                Nothing recorded under this filter yet.
              </li>
            )}
          </ul>
        )}
      </main>
    </div>
  );
}
